import { reportAdEvents, type AdEvent } from '@/api/ads';

/**
 * Batches ad impressions and clicks, then flushes them to the API periodically.
 *
 * Reporting per-view would fire a request every time a card scrolls into sight,
 * which on a feed is both wasteful and unreliable on a flaky connection. Instead
 * counts accumulate in memory per placement and go out in one request every
 * FLUSH_INTERVAL_MS (or immediately on flushNow, e.g. when the app backgrounds).
 *
 * Losing a batch is acceptable and deliberate: an under-count of impressions is
 * far better than blocking the UI or retrying forever, and the server bills on
 * what it received. Failed flushes put their counts back so the next flush can
 * carry them, but nothing is persisted across an app restart.
 */
const FLUSH_INTERVAL_MS = 15_000;

type Pending = Map<string, { impressions: number; clicks: number }>;

const pending: Pending = new Map();
let timer: ReturnType<typeof setInterval> | null = null;
let currentToken: string | null = null;

function bump(placementID: string, field: 'impressions' | 'clicks') {
  const entry = pending.get(placementID) ?? { impressions: 0, clicks: 0 };
  entry[field] += 1;
  pending.set(placementID, entry);
}

/** Records that an ad was actually seen. Call once per placement per render —
 *  the caller is responsible for not double-counting a single view. */
export function trackImpression(placementID: string) {
  bump(placementID, 'impressions');
}

/** Records a tap on an ad's call-to-action. */
export function trackClick(placementID: string) {
  bump(placementID, 'clicks');
  // A click is rare and high-value, so don't make it wait for the timer.
  void flushNow();
}

/** Drains the queue and sends it. Safe to call when there's nothing pending. */
export async function flushNow() {
  if (!currentToken || pending.size === 0) return;

  const events: AdEvent[] = [];
  pending.forEach((counts, placement_id) => {
    events.push({ placement_id, ...counts });
  });
  pending.clear();

  try {
    await reportAdEvents(currentToken, events);
  } catch {
    // Put the counts back so the next flush can retry them. Silent by design:
    // a failed analytics report must never surface to a fan.
    events.forEach((e) => {
      const entry = pending.get(e.placement_id) ?? { impressions: 0, clicks: 0 };
      entry.impressions += e.impressions;
      entry.clicks += e.clicks;
      pending.set(e.placement_id, entry);
    });
  }
}

/** Starts (or re-points) the flush loop. Called on sign-in; the token changes
 *  when the session does, so the tracker holds the latest one. */
export function startAdTracker(token: string) {
  currentToken = token;
  if (timer) return;
  timer = setInterval(() => void flushNow(), FLUSH_INTERVAL_MS);
}

/** Stops the loop and drops anything queued — on sign-out there is no longer a
 *  token to attribute the report to. */
export function stopAdTracker() {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
  currentToken = null;
  pending.clear();
}
